import IconButton from '@/components/common/icon-button';
import CloseIcon from '@/components/icons/CloseIcon';
import { useScaleSize } from '@/hooks';
import { useFocusTrap } from '@/hooks/modal';
import { useModalStackStore } from '@/stores';
import clsx from 'clsx';
import { useEffect, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './styles.module.scss';

type ClosePlacement = 'aside' | 'below-right';

type Props = {
    children?: React.ReactNode;
    isOpen?: boolean;
    onClose?: () => void;
    slides?: React.ReactNode;
    contentClassName?: string;
    closeIconTabIndex?: number;
    closeIconRegionOrder?: number;
    closePlacement?: ClosePlacement;
    delayTimePushModal?: number;
    modalIdInit?: string;
    isLoading?: boolean;
    zIndex?: number;
};

export const CarouselModal = ({
    children,
    isOpen = false,
    onClose = () => {},
    slides,
    contentClassName,
    closeIconTabIndex,
    closeIconRegionOrder,
    closePlacement = 'aside',
    delayTimePushModal = 0,
    modalIdInit,
    isLoading = false,
    zIndex = 60,
}: Props) => {
    const generatedId = useId();
    const modalId = modalIdInit ? modalIdInit : generatedId;
    const modalRef = useRef<HTMLDivElement>(null);
    const slideRef = useRef<HTMLElement | null>(null);
    const contentRef = useRef<HTMLElement | null>(null);
    const { t } = useTranslation();
    const { scaleWidth, scaleHeight } = useScaleSize();
    const { pushModal, popModal, isModalInStack } = useModalStackStore();
    const prevActiveElementRef = useRef<HTMLElement | null>(null);
    const isCloseBelowRight = closePlacement === 'below-right';

    useEffect(() => {
        if (isOpen) {
            setTimeout(() => {
                if (!isModalInStack(modalId)) {
                    pushModal(modalId);
                }
            }, delayTimePushModal);
        } else {
            if (isModalInStack(modalId)) {
                popModal(modalId);
            }
        }

        return () => {
            if (isModalInStack(modalId)) {
                popModal(modalId);
            }
        };
    }, [isOpen, modalId, delayTimePushModal]);

    useEffect(() => {
        if (isOpen && !prevActiveElementRef.current) {
            prevActiveElementRef.current = document.activeElement as HTMLElement;
        }
    }, [isOpen]);

    const handleClose = () => {
        const activeElement = document.querySelector('.poi-active') as HTMLElement;
        if (activeElement) {
            activeElement.focus({ preventScroll: false });
            activeElement.classList.remove('poi-active');
        } else if (prevActiveElementRef.current) {
            const elementToRestore = prevActiveElementRef.current;
            prevActiveElementRef.current = null;

            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    if (document.body.contains(elementToRestore) && elementToRestore.focus) {
                        try {
                            elementToRestore.focus({ preventScroll: false });
                        } catch (error) {
                            // ignore focus restore errors
                        }
                    }
                });
            });
        }

        popModal(modalId);
        onClose();
    };

    useFocusTrap(modalRef, isOpen, handleClose, isLoading, modalId);

    const closeButton = (
        <IconButton
            role="button"
            onClick={handleClose}
            ariaLabel={t('common.dialog.closeModal')}
            icon={
                isCloseBelowRight ? (
                    <CloseIcon color="white" width={19} height={19} />
                ) : (
                    <CloseIcon color="white" width={32} height={32} />
                )
            }
            width={scaleWidth(isCloseBelowRight ? 80 : 60)}
            height={scaleHeight(isCloseBelowRight ? 80 : 60)}
            tabIndex={closeIconTabIndex ?? 999}
            data-playsound="ting"
            data-interactive
            data-modal-region
            data-modal-region-order={closeIconRegionOrder ?? 999}
        />
    );

    if (!isOpen) {
        return null;
    }

    return (
        <div
            style={{
                position: 'fixed',
                inset: 0,
                overflow: 'auto',
                zIndex: zIndex,
            }}
        >
            <div className={styles['carousel-modal__wrapper']} role="dialog" aria-modal="true">
                <section
                    className={clsx(isCloseBelowRight ? styles['carousel-modal__body-wrap'] : styles['carousel-modal__container'])}
                    ref={modalRef}
                >
                    {slides && (
                        <section
                            ref={slideRef}
                            className={clsx(styles['carousel-modal__slide'], isCloseBelowRight && styles['carousel-modal__slide--below-right'])}
                        >
                            {slides}
                        </section>
                    )}
                    {children && (
                        <section
                            ref={contentRef}
                            className={clsx(
                                'widescreen:w-[calc(656*var(--vw-unit))] relative w-full self-center',
                                !isCloseBelowRight && 'w-[calc(680*var(--vw-unit))]',
                                !isCloseBelowRight && !slides && 'widescreen:pb-0 fullscreen:pb-0 pb-[calc(320*var(--vh-unit))]'
                            )}
                        >
                            <div className={clsx(styles['carousel-modal__content'], 'bg-bg-white', contentClassName && contentClassName)}>
                                {children}
                            </div>
                            {!isCloseBelowRight && (
                                <div
                                    className={`absolute right-[calc(-76*var(--vw-unit))] ${slides ? 'bottom-0' : 'widescreen:bottom-0 fullscreen:bottom-0 bottom-[calc(320*var(--vh-unit))]'}`}
                                >
                                    {closeButton}
                                </div>
                            )}
                        </section>
                    )}
                    {isCloseBelowRight && <div className={styles['carousel-modal__close--below-right']}>{closeButton}</div>}
                </section>
            </div>
        </div>
    );
};
